import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, addDoc, serverTimestamp, getDocs, doc, updateDoc, increment } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { Layout, Breadcrumb, Menu, theme, Button, Input, Modal, List, Avatar, Alert, Select, Space } from 'antd';
import { MenuOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { auth, db } from '../firebase';
import LoginModal from '../auth/LoginModal';
import './forum.css';

const { Header, Content, Sider } = Layout;
const { Option } = Select;

function Forum() {
  const [user] = useAuthState(auth);
  const [forums, setForums] = useState([]);
  const [selectedForum, setSelectedForum] = useState(null);
  const [showNewForumModal, setShowNewForumModal] = useState(false);
  const [newForumName, setNewForumName] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(!user);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [siderCollapsed, setSiderCollapsed] = useState(window.innerWidth <= 768);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  useEffect(() => {
    if (user) {
      fetchForums();
    }
  }, [user]);
  
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSiderCollapsed(true);
      } else {
        setSiderCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to initialize
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };

  const fetchForums = async () => {
    try {
      const forumsRef = collection(db, 'forums');
      const forumsSnapshot = await getDocs(query(forumsRef, orderBy('createdAt', 'desc')));
      
      // Get forums with enhanced data
      const forumsList = [];
      
      for (const doc of forumsSnapshot.docs) {
        const forumData = { id: doc.id, ...doc.data() };
        
        // Get message count if not already available or need to refresh
        try {
          const messagesRef = collection(db, 'forums', doc.id, 'messages');
          const messagesSnapshot = await getDocs(messagesRef);
          forumData.messageCount = messagesSnapshot.size;
        } catch (err) {
          console.error("Error fetching messages:", err);
          forumData.messageCount = 0;
        }
        
        // Get user count for each forum
        try {
          const messagesRef = collection(db, 'forums', doc.id, 'messages');
          const messagesSnapshot = await getDocs(messagesRef);
          
          // Extract unique user IDs from messages
          const uniqueUsers = new Set();
          messagesSnapshot.docs.forEach(messageDoc => {
            const { uid } = messageDoc.data();
            if (uid) uniqueUsers.add(uid);
          });
          
          forumData.userCount = uniqueUsers.size || 0;
        } catch (err) {
          console.error("Error counting users:", err);
          forumData.userCount = 0;
        }
        
        forumsList.push(forumData);
      }
      
      // Sort forums by message count (descending)
      forumsList.sort((a, b) => (b.messageCount || 0) - (a.messageCount || 0));
      
      setForums(forumsList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching forums:", error);
      setError("Failed to fetch forums. Please try again.");
      setLoading(false);
    }
  };

  const createNewForum = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (newForumName.trim()) {
      try {
        const docRef = await addDoc(collection(db, 'forums'), {
          name: newForumName,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          lastMessage: '',
          lastMessageTime: serverTimestamp(),
          messageCount: 0
        });
        console.log("New forum created:", docRef.id);
        setShowNewForumModal(false);
        setNewForumName('');
        await fetchForums();
      } catch (error) {
        console.error("Error creating new forum:", error);
        setError("Failed to create new forum. Please try again.");
      }
    }
  };
  
  const handleForumSelect = (forum) => {
    setSelectedForum(forum);
    if (isMobile) {
      setSiderCollapsed(true);
    }
  };
  
  const handleBackToForums = () => {
    if (isMobile) {
      setSelectedForum(null);
    }
  };

  if (!user) {
    return (
      <Layout>
        <Content style={{ padding: '0 16px' }}>
          <Alert message="Please log in to access the forums" type="info" />
          <LoginModal 
            show={showLoginModal}
            onHide={() => setShowLoginModal(false)}
            onLoginSuccess={handleLoginSuccess}
          />
        </Content>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <Content style={{ padding: '0 16px' }}>
          <div>Loading forums...</div>
        </Content>
      </Layout>
    );
  }

  // Mobile forum selector dropdown
  const MobileForumSelector = () => (
    <div className="mobile-forum-selector">
      <Select
        placeholder="Select a forum"
        value={selectedForum?.id}
        onChange={(value) => {
          const forum = forums.find(f => f.id === value);
          setSelectedForum(forum);
        }}
        style={{ width: '100%' }}
      >
        {forums.map(forum => (
          <Option key={forum.id} value={forum.id}>
            {forum.name || 'Untitled Forum'} ({forum.messageCount || 0})
          </Option>
        ))}
      </Select>
      <Button 
        type="primary"
        onClick={() => setShowNewForumModal(true)}
        className="new-forum-button mobile-new-forum"
        style={{
          backgroundColor: '#ffffff', 
          color: '#333333', 
          border: '1px solid #e8e8e8',
          paddingLeft: '20px',
          paddingRight: '20px',
          textAlign: 'center'
        }}
      >
        New Forum
      </Button>
    </div>
  );

  // Replace the mobile forums list rendering with this improved version
  const renderMobileView = () => {
    if (selectedForum) {
      return (
        <div className="mobile-chat-container">
          <div className="mobile-chat-header">
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToForums}
              className="back-button"
            />
            <h3>{selectedForum.name}</h3>
          </div>
          <ChatRoom 
            forumId={selectedForum.id} 
            forumName={selectedForum.name}
            onMessageSent={fetchForums}
            isMobile={true}
          />
        </div>
      );
    } else {
      return (
        <div className="mobile-forums-list" style={{backgroundColor: '#ffffff'}}>
          <div className="mobile-forum-selector" style={{backgroundColor: '#ffffff'}}>
            <h3 className="mobile-forums-title" style={{color: '#333333'}}>Totly Forums</h3>
            <Button 
              type="primary"
              onClick={() => setShowNewForumModal(true)}
              className="new-forum-button mobile-new-forum"
              style={{
                backgroundColor: '#ffffff', 
                color: '#333333', 
                border: '1px solid #e8e8e8',
                paddingLeft: '20px',
                paddingRight: '20px',
                textAlign: 'center'
              }}
            >
              New Forum
            </Button>
          </div>
          <List
            className="mobile-forum-list"
            dataSource={forums}
            style={{backgroundColor: '#ffffff'}}
            renderItem={forum => (
              <List.Item 
                onClick={() => handleForumSelect(forum)}
                className="mobile-forum-item"
                style={{backgroundColor: '#ffffff', color: '#333333', display: 'flex', justifyContent: 'space-between'}}
              >
                <List.Item.Meta
                  style={{textAlign: 'left'}}
                  title={<span style={{color: '#333333', textAlign: 'left'}}>{forum.name || 'Untitled Forum'}</span>}
                  description={
                    <div className="forum-stats" style={{color: '#666666', textAlign: 'left'}}>
                      {forum.userCount || 0} {forum.userCount === 1 ? 'User' : 'Users'} · {forum.messageCount || 0} {forum.messageCount === 1 ? 'Message' : 'Messages'}
                    </div>
                  }
                />
                <div className="message-count-badge" style={{
                  backgroundColor: '#e8e8e8', 
                  color: '#333333', 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {forum.messageCount || 0}
                </div>
              </List.Item>
            )}
          />
        </div>
      );
    }
  };

  // Update desktop forum menu items to match the screenshot
  const renderDesktopMenuItems = () => {
    return forums.map((forum) => (
      <Menu.Item
        key={forum.id}
        onClick={() => handleForumSelect(forum)}
        className="forum-list-item"
      >
        <div className="forum-item-content">
          <span className="forum-name">{forum.name || 'Untitled Forum'}</span>
          <span className="message-count-badge desktop-badge">
            {forum.messageCount || 0}
          </span>
        </div>
      </Menu.Item>
    ));
  };

  return (
    <Layout className="forum-main-layout">
      <Content style={{ padding: isMobile ? '0 8px' : '0 48px' }}>
        {!isMobile && (
          <Breadcrumb style={{ margin: '16px 0' }}>
            <Breadcrumb.Item>Totly Forums</Breadcrumb.Item>
            {selectedForum && <Breadcrumb.Item>{selectedForum.name}</Breadcrumb.Item>}
          </Breadcrumb>
        )}
        
        <Layout style={{ 
          padding: isMobile ? '8px 0' : '24px 0', 
          background: colorBgContainer, 
          borderRadius: borderRadiusLG
        }}>
          {/* For mobile: Show forums list or chat based on selection */}
          {isMobile ? (
            renderMobileView()
          ) : (
            // Desktop layout with sider
            <>
              <Sider 
                width={300} 
                className="forum-sider"
                collapsible
                collapsed={siderCollapsed}
                onCollapse={value => setSiderCollapsed(value)}
                collapsedWidth={0}
                breakpoint="lg"
                trigger={null}
              >
                <div className="forum-button-container">
                  <Button type="primary" onClick={() => setShowNewForumModal(true)} className="new-forum-button">
                    New Forum
                  </Button>
                </div>
                <Menu
                  mode="inline"
                  selectedKeys={[selectedForum?.id]}
                  style={{ borderRight: 0 }}
                >
                  {renderDesktopMenuItems()}
                </Menu>
              </Sider>
              
              <Content style={{ padding: '0 24px', minHeight: 280 }}>
                {siderCollapsed && (
                  <Button 
                    type="text" 
                    icon={<MenuOutlined />} 
                    onClick={() => setSiderCollapsed(false)}
                    style={{ marginBottom: 16 }}
                    className="toggle-sider-button"
                  />
                )}
                {selectedForum ? (
                  <ChatRoom 
                    forumId={selectedForum.id} 
                    forumName={selectedForum.name}
                    onMessageSent={fetchForums}
                    isMobile={isMobile}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p>Select a forum to start chatting</p>
                  </div>
                )}
              </Content>
            </>
          )}
        </Layout>
      </Content>

      <Modal
        title="Create New Forum"
        open={showNewForumModal}
        onCancel={() => setShowNewForumModal(false)}
        footer={null}
        centered
        className="mobile-create-forum-modal"
        width="90%"
        style={{ maxWidth: '320px' }}
        wrapClassName="forum-modal-wrap"
        bodyStyle={{ padding: '16px' }}
        destroyOnClose={true}
        maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      >
        <form onSubmit={createNewForum}>
          <Input
            value={newForumName}
            onChange={(e) => setNewForumName(e.target.value)}
            placeholder="Enter forum name"
            required
            style={{
              height: '40px',
              fontSize: '16px',
              padding: '8px 12px',
              marginBottom: '20px',
              width: '100%'
            }}
          />
          <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
            <Button 
              onClick={() => setShowNewForumModal(false)} 
              className="modal-cancel-button"
              style={{ paddingLeft: '20px', paddingRight: '20px' }}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              className="create-forum-button"
              style={{ paddingLeft: '20px', paddingRight: '20px' }}
            >
              Create Forum
            </Button>
          </div>
        </form>
      </Modal>

      <LoginModal 
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Layout>
  );
}

function ChatRoom({ forumId, forumName, onMessageSent, isMobile }) {
  const dummy = useRef();
  const messagesContainerRef = useRef();
  const messagesRef = collection(db, 'forums', forumId, 'messages');
  // Get all messages without ordering to include both old and new formats
  const [user] = useAuthState(auth);
  const [allMessages] = useCollectionData(messagesRef, { idField: 'id' });
  
  // Sort messages on client side to handle both timestamp and createdAt fields
  const messages = allMessages ? allMessages.sort((a, b) => {
    const timeA = a.timestamp || a.createdAt;
    const timeB = b.timestamp || b.createdAt;
    if (!timeA || !timeB) return 0;
    return timeA.toMillis() - timeB.toMillis();
  }) : [];
  const [formValue, setFormValue] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages && messages.length > 0) {
      scrollToBottom(false);
    }
  }, [messages]);
  
  // Adjust scroll when component mounts
  useEffect(() => {
    // Short timeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      scrollToBottom(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const scrollToBottom = (smooth = true) => {
    if (dummy.current) {
      dummy.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (!formValue.trim()) return;

    // Store the message text before clearing the form
    const messageText = formValue.trim();
    // Clear the form immediately for better UX
    setFormValue('');

    try {
      const { uid, photoURL, displayName } = user;
      
      await addDoc(messagesRef, {
        content: messageText,
        timestamp: serverTimestamp(),
        uid,
        photoURL,
        displayName
      });

      // Update the forum's last message and increment message count
      const forumRef = doc(db, 'forums', forumId);
      await updateDoc(forumRef, {
        lastMessage: messageText,
        lastMessageTime: serverTimestamp(),
        messageCount: increment(1)
      });

      if (onMessageSent) onMessageSent();
      
      // Don't use setTimeout here - it causes the centering issue
      // Just scroll immediately
      scrollToBottom(false);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className={`chat-room ${isMobile ? 'mobile-chat-room' : ''}`}>
      {!isMobile && <h3 className='chat-header'>{forumName}</h3>}
      <div className="messages-container" ref={messagesContainerRef}>
        {messages && messages.length > 0 ? (
          messages.map(msg => (
            <ChatMessage key={msg.id} message={msg} currentUser={user} />
          ))
        ) : (
          <div className="no-messages">
            <p>No messages yet. Be the first to start the conversation!</p>
          </div>
        )}
        <div ref={dummy} className="dummy-div"></div>
      </div>
      <form onSubmit={sendMessage} className="message-input-form">
        <input
          className="message-input"
          value={formValue}
          onChange={(e) => setFormValue(e.target.value)}
          placeholder="Type your message here"
          autoComplete="off"
        />
        <button type="submit" className="send-button" disabled={!formValue.trim()}>
          Send
        </button>
      </form>

      {/* Login Modal */}
      <LoginModal 
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLoginSuccess={() => setShowLoginModal(false)}
      />
    </div>
  );
}

function ChatMessage({ message, currentUser }) {
  const { content, text, uid, displayName } = message;
  const messageClass = uid === currentUser?.uid ? 'sent' : 'received';
  
  // Handle both old (text) and new (content) message formats
  const messageText = content || text;
  // Handle both old (createdAt) and new (timestamp) date formats
  const messageTime = message.timestamp || message.createdAt;

  return (
    <div className={`message ${messageClass}`}>
      <div className="message-content">
        <div className="message-header">
          {displayName && <span className="message-author">{displayName}</span>}
        </div>
        <p>{messageText}</p>
        {messageTime && (
          <span className="message-timestamp">
            {messageTime.toDate().toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
}

export default Forum;